import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowRightLeft, Loader2, Search, Trash2 } from 'lucide-react';
import Drawer from '../../components/Drawer';
import Button from '../../components/Button';
import AuthMessage from '../../components/AuthMessage';
import { useAuth } from '../../context/AuthContext';
import { listAllProducts, reassignProductCategory } from '../../api/inventory';
import form from '../../styles/form.module.css';
import styles from './ReassignCategoryDrawer.module.css';

const MOVE_CONCURRENCY = 5;

function chunk(items, size) {
  const out = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
}

function errorMessage(err) {
  if (err?.code === 'unknown_category') return 'Pick a valid category';
  if (err?.code === 'forbidden') return 'Ops/Admin only';
  if (err?.code === 'not_found') return 'Already removed — refresh';
  return err?.detail || err?.message || 'Could not move this product.';
}

function CategoryCombobox({ categories, categoryId, onChange, disabled }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const wrapRef = useRef(null);
  const selected = categories.find((c) => c.id === categoryId);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return categories;
    return categories.filter(
      (c) => c.name.toLowerCase().includes(q) || (c.id || '').toLowerCase().includes(q),
    );
  }, [categories, query]);

  useEffect(() => setHighlight(0), [query, open]);

  useEffect(() => {
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const selectCategory = (c) => {
    onChange(c.id);
    setQuery('');
    setOpen(false);
  };

  const handleKeyDown = (e) => {
    if (!open) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (filtered[highlight]) selectCategory(filtered[highlight]);
    } else if (e.key === 'Escape') {
      setOpen(false);
      setQuery('');
    }
  };

  return (
    <div className={`${styles.comboWrap} ${disabled ? styles.comboDisabled : ''}`} ref={wrapRef}>
      <Search size={14} className={styles.comboIcon} />
      <input
        className={`${form.input} ${styles.comboInput}`}
        value={open ? query : (selected?.name || '')}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => { if (!disabled) { setOpen(true); setQuery(''); } }}
        onKeyDown={handleKeyDown}
        placeholder={categories.length === 0 ? 'No other active categories' : 'Search categories…'}
        role="combobox"
        aria-expanded={open}
        aria-controls="reassign-category-list"
        autoComplete="off"
        disabled={disabled}
      />
      {open && !disabled && (
        <div id="reassign-category-list" className={styles.comboDropdown} role="listbox">
          {filtered.length === 0 ? (
            <div className={styles.comboEmpty}>
              {categories.length === 0 ? 'No other active categories' : `No categories match "${query.trim()}"`}
            </div>
          ) : (
            filtered.map((c, i) => (
              <button
                type="button"
                key={c.id}
                role="option"
                aria-selected={c.id === categoryId}
                className={`${styles.comboOption} ${i === highlight ? styles.comboOptionActive : ''}`}
                onMouseDown={(e) => { e.preventDefault(); selectCategory(c); }}
                onMouseEnter={() => setHighlight(i)}
              >
                <span className={styles.comboOptionName}>{c.name}</span>
                <span className={styles.comboOptionMeta}>{c.id}</span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

export default function ReassignCategoryDrawer({
  open,
  onClose,
  sourceCategory,
  productCountHint,
  categories,
  onDeleted,
  onProductsMoved,
}) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [targetId, setTargetId] = useState('');
  const [movingIds, setMovingIds] = useState(() => new Set());
  const [rowErrors, setRowErrors] = useState({});
  const [bulkProgress, setBulkProgress] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [actionError, setActionError] = useState('');

  const sourceId = sourceCategory?.id || '';
  const sourceName = sourceCategory?.name || sourceId;

  const targets = useMemo(
    () => categories.filter((c) => c.status === 'ACTIVE' && c.id !== sourceId),
    [categories, sourceId],
  );

  const loadItems = useCallback(async () => {
    if (!user?.idToken || !sourceId) return;
    setLoading(true);
    setLoadError('');
    setActionError('');
    setRowErrors({});
    try {
      const list = await listAllProducts(user.idToken, {
        category: sourceId,
        includeInactive: true,
      });
      setItems(list);
      const activeTargets = categories.filter((c) => c.status === 'ACTIVE' && c.id !== sourceId);
      setTargetId((prev) => {
        if (prev && activeTargets.some((t) => t.id === prev)) return prev;
        return activeTargets.find((t) => t.id === 'general')?.id || activeTargets[0]?.id || '';
      });
    } catch (err) {
      setLoadError(err.message || 'Could not load products in this category.');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [user?.idToken, sourceId, categories]);

  useEffect(() => {
    if (!open) return;
    loadItems();
    // Reload when sheet opens or source category changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, sourceId]);


  const setMoving = (id, on) => {
    setMovingIds((prev) => {
      const next = new Set(prev);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const moveOne = async (item, toCategoryId = targetId) => {
    if (!toCategoryId || !user?.idToken) return false;
    setMoving(item.id, true);
    setRowErrors((prev) => {
      const next = { ...prev };
      delete next[item.id];
      return next;
    });
    setActionError('');
    try {
      await reassignProductCategory(item.id, toCategoryId, user.idToken);
      setItems((prev) => prev.filter((p) => p.id !== item.id));
      onProductsMoved?.([{ id: item.id, category: toCategoryId }]);
      return true;
    } catch (err) {
      setRowErrors((prev) => ({ ...prev, [item.id]: errorMessage(err) }));
      return false;
    } finally {
      setMoving(item.id, false);
    }
  };

  const moveAll = async () => {
    if (!targetId || !items.length || !user?.idToken) return;
    setActionError('');
    setBulkProgress({ done: 0, total: items.length });
    const failed = [];
    const snapshot = [...items];
    let done = 0;

    for (const batch of chunk(snapshot, MOVE_CONCURRENCY)) {
      // eslint-disable-next-line no-await-in-loop
      const results = await Promise.all(
        batch.map(async (item) => {
          setMoving(item.id, true);
          try {
            await reassignProductCategory(item.id, targetId, user.idToken);
            return { ok: true, item };
          } catch (err) {
            return { ok: false, item, error: errorMessage(err) };
          } finally {
            setMoving(item.id, false);
            done += 1;
            setBulkProgress({ done, total: snapshot.length });
          }
        }),
      );

      const succeeded = [];
      results.forEach((r) => {
        if (r.ok) succeeded.push(r.item);
        else {
          failed.push(r.item);
          setRowErrors((prev) => ({ ...prev, [r.item.id]: r.error }));
        }
      });

      if (succeeded.length) {
        const ids = new Set(succeeded.map((s) => s.id));
        setItems((prev) => prev.filter((p) => !ids.has(p.id)));
        onProductsMoved?.(succeeded.map((s) => ({ id: s.id, category: targetId })));
      }
    }

    setBulkProgress(null);
    if (failed.length) {
      setActionError(`${failed.length} product${failed.length === 1 ? '' : 's'} could not be moved. Retry failed rows.`);
    }
  };

  const handleDelete = async () => {
    if (items.length || !onDeleted) return;
    setDeleting(true);
    setActionError('');
    try {
      await onDeleted(sourceId);
      onClose?.();
    } catch (err) {
      if (err?.code === 'category_in_use') {
        setActionError(`Move ${err.productCount ?? 'remaining'} products first`);
        await loadItems();
      } else if (err?.code === 'category_protected') {
        setActionError("General can't be deleted");
      } else if (err?.code === 'not_found') {
        setActionError('Already removed — refresh');
        onClose?.();
      } else if (err?.code === 'forbidden') {
        setActionError('Ops/Admin only');
      } else {
        setActionError(err?.detail || err?.message || 'Could not delete this category.');
      }
    } finally {
      setDeleting(false);
    }
  };

  const busy = loading || bulkProgress != null || deleting || movingIds.size > 0;
  const canMove = Boolean(targetId) && items.length > 0 && !busy;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={`Move products out of ${sourceName}`}
      subtitle={
        loading
          ? 'Loading products…'
          : `${items.length || productCountHint || 0} product${(items.length || productCountHint || 0) === 1 ? '' : 's'} must be moved before delete`
      }
      width={520}
      footer={(
        <div className={styles.footerRow}>
          {items.length === 0 && !loading ? (
            <Button icon={Trash2} onClick={handleDelete} disabled={deleting || !onDeleted || bulkProgress != null}>
              {deleting ? 'Deleting…' : 'Delete category'}
            </Button>
          ) : (
            <Button onClick={moveAll} disabled={!canMove}>
              {bulkProgress
                ? `Moving ${bulkProgress.done} / ${bulkProgress.total}…`
                : 'Move all products'}
            </Button>
          )}
          <Button variant="outline" onClick={onClose} disabled={bulkProgress != null || deleting}>
            Close
          </Button>
        </div>
      )}
    >
      <AuthMessage>{loadError || actionError}</AuthMessage>

      <label className={form.field}>
        <span className={form.label}>Move to</span>
        <CategoryCombobox
          categories={targets}
          categoryId={targetId}
          onChange={setTargetId}
          disabled={busy || targets.length === 0}
        />
        <span className={form.hint}>ACTIVE categories only. Uses category slug in the API.</span>
      </label>

      {loading ? (
        <div className={styles.skeletonList} aria-busy="true">
          {[0, 1, 2, 3].map((i) => <div key={i} className={styles.skeletonRow} />)}
        </div>
      ) : items.length === 0 ? (
        <div className={styles.emptyState}>
          <p className={styles.emptyTitle}>All products moved</p>
          <p className={styles.emptyBody}>You can delete this category now.</p>
        </div>
      ) : (
        <div className={styles.table}>
          <div className={styles.tableHead}>
            <span>Product</span>
            <span>Status</span>
            <span />
          </div>
          {items.map((item) => {
            const moving = movingIds.has(item.id);
            return (
              <div key={item.id} className={styles.tableRow}>
                <div className={styles.productCell}>
                  <p className={styles.productName}>{item.name}</p>
                  <p className={styles.productMeta}>
                    {item.sku || item.id}
                    {item.status === 'INACTIVE' ? ' · inactive' : ''}
                  </p>
                  {rowErrors[item.id] && (
                    <p className={styles.rowError}>{rowErrors[item.id]}</p>
                  )}
                </div>
                <span className={`${styles.statusChip} ${item.status === 'ACTIVE' ? styles.active : styles.inactive}`}>
                  {item.status === 'ACTIVE' ? 'Active' : item.status === 'INACTIVE' ? 'Inactive' : item.status}
                </span>
                <button
                  type="button"
                  className={styles.moveBtn}
                  disabled={!targetId || moving || bulkProgress != null}
                  onClick={() => moveOne(item)}
                >
                  {moving ? <Loader2 size={13} className={styles.spin} /> : <ArrowRightLeft size={13} />}
                  {moving ? 'Moving…' : 'Move'}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </Drawer>
  );
}
